import {
  AgentBatchReadPayload,
  AgentBatchReadResponse,
  AgentEvaluationDetail,
  AgentEvaluationReportDetail,
  AgentEvaluationReportRerunResponse,
  AgentEvaluationReportsListResponse,
  AgentEvaluationReportType,
  AgentEvaluationRunDetail,
  AgentModelOption,
  AgentSessionDetailResponse,
  AgentSessionEventsResponse,
  AgentSessionsResponse,
  AgentTypesResponse,
  Answers,
  BuildEvalFromDescriptionResponse,
  ChatTranscriptResponse,
  CreateEvaluationPayload,
  CreateSessionPayload,
  CreateSessionResponse,
  EnhancedAgentSessionMetrics,
  EvaluationOrganizationOption,
  EvaluationRunStatus,
  EvaluationSessionConfig,
  EvaluationUserLookup,
  GenerateEvalFromSessionResponse,
  GetAgentEvaluationPickerResponse,
  GetEvaluationRunsResponse,
  GetEvaluationsResponse,
  RunEvaluationResponse,
  RunEvaluationsBatchSelection,
  SendMessagePayload,
  SendMessageResponse,
  UpdateEvaluationPayload,
} from "@/types/agent"
import { FlightLegApi } from "@/types/flights/flight-leg"
import { FlightOption } from "@/types/flights/flight-option"
import { FlightSliceApi } from "@/types/flights/flight-slice-api"
import { AgentStatusSnapshotResponse } from "@/types/socket"

import { BaseApi } from "./base-api"

export class AgentApi extends BaseApi {
  async getSessions(params?: {
    page?: number
    per_page?: number
    status?: string
    include_eval_sessions?: boolean
    agent_type?: string
    orgid?: string
    visibility?: "mine" | "shared"
    sort_by?: string
  }): Promise<AgentSessionsResponse> {
    return this.get<AgentSessionsResponse>("/api/agent/sessions", { params })
  }

  async getSession(id: number): Promise<AgentSessionDetailResponse> {
    return this.get<AgentSessionDetailResponse>(`/api/agent/sessions/${id}`)
  }

  async createSession(
    payload: CreateSessionPayload
  ): Promise<CreateSessionResponse> {
    return this.post<CreateSessionResponse, CreateSessionPayload>(
      "/api/agent/sessions",
      payload
    )
  }

  async sendMessage(
    sessionId: number,
    payload: SendMessagePayload
  ): Promise<SendMessageResponse> {
    return this.post<SendMessageResponse, SendMessagePayload>(
      `/api/agent/sessions/${sessionId}/messages`,
      payload
    )
  }

  async getAgentTypes(): Promise<AgentTypesResponse> {
    return this.get<AgentTypesResponse>("/api/agent/types")
  }

  async getModels(): Promise<AgentModelOption[]> {
    return this.get<AgentModelOption[]>("/api/agent/models")
  }

  async getSessionEvents(
    sessionId: number,
    params?: {
      limit?: number
      before_id?: number
      after_id?: number
    }
  ): Promise<AgentSessionEventsResponse> {
    return this.get<AgentSessionEventsResponse>(
      `/api/agent/sessions/${sessionId}/events`,
      { params }
    )
  }

  async getSessionMetrics(
    sessionId: number
  ): Promise<EnhancedAgentSessionMetrics> {
    return this.get<EnhancedAgentSessionMetrics>(
      `/api/agent/sessions/${sessionId}/metrics`
    )
  }

  async getSessionStatusSnapshot(
    sessionId: number
  ): Promise<AgentStatusSnapshotResponse> {
    return this.get<AgentStatusSnapshotResponse>(
      `/api/agent/sessions/${sessionId}/status_snapshot`
    )
  }

  async stopSession(sessionId: number): Promise<void> {
    return this.post<void, undefined>(
      `/api/agent/sessions/${sessionId}/stop`,
      undefined
    )
  }

  async approvePlan(sessionId: number): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }, undefined>(
      `/api/agent/sessions/${sessionId}/approve_plan`,
      undefined
    )
  }

  async rejectPlan(
    sessionId: number,
    reason: string
  ): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }, { reason: string }>(
      `/api/agent/sessions/${sessionId}/reject_plan`,
      { reason }
    )
  }

  async answerQuestions(
    sessionId: number,
    questionId: number,
    answers: Answers
  ): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }, { answers: Answers }>(
      `/api/agent/sessions/${sessionId}/questions/${questionId}/answer`,
      { answers }
    )
  }

  async skipQuestions(
    sessionId: number,
    questionId: number
  ): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }, undefined>(
      `/api/agent/sessions/${sessionId}/questions/${questionId}/skip`,
      undefined
    )
  }

  async setShared(
    sessionId: number,
    shared: boolean
  ): Promise<{ shared: boolean }> {
    return this.patch<{ shared: boolean }, { shared: boolean }>(
      `/api/agent/sessions/${sessionId}/share`,
      { shared }
    )
  }

  async getSandboxTables(sessionId: number): Promise<{
    tables: Array<{
      name: string
      row_count: number
      columns: Array<{
        name: string
        type: string
        sql_type: string
      }>
    }>
  }> {
    return this.get<{
      tables: Array<{
        name: string
        row_count: number
        columns: Array<{
          name: string
          type: string
          sql_type: string
        }>
      }>
    }>(`/api/agent/sessions/${sessionId}/sandbox/tables`)
  }

  async getSandboxTableData(
    sessionId: number,
    tableName: string,
    params?: {
      page?: number
      per_page?: number
      sort_column?: string
      sort_direction?: "asc" | "desc"
      filters?: Record<string, string>
    }
  ): Promise<{
    data: Array<Record<string, unknown>>
    columns: Array<{
      name: string
      type: string
      sql_type: string
    }>
    total_count: number
    page: number
    per_page: number
    total_pages: number
  }> {
    return this.get<{
      data: Array<Record<string, unknown>>
      columns: Array<{
        name: string
        type: string
        sql_type: string
      }>
      total_count: number
      page: number
      per_page: number
      total_pages: number
    }>(`/api/agent/sessions/${sessionId}/sandbox/tables/${tableName}`, {
      params,
    })
  }

  async getSandboxHotelData(
    sessionId: number,
    hotelEntityId: string,
    checkinDate: string,
    checkoutDate: string
  ): Promise<SandboxHotelDataResponse> {
    return this.get<SandboxHotelDataResponse>(
      `/api/agent/sessions/${sessionId}/sandbox/hotel_availability`,
      {
        params: {
          entity_id: hotelEntityId,
          checkin_date: checkinDate,
          checkout_date: checkoutDate,
        },
      }
    )
  }

  async getMemoryReport(sessionId: number): Promise<MemoryReportResponse> {
    return this.get<MemoryReportResponse>(
      `/api/agent/sessions/${sessionId}/sandbox/memory_report`
    )
  }

  async getReportData(
    sessionId: number,
    reportId: number,
    selectedParams?: Record<string, string>
  ): Promise<ReportDataResponse> {
    return this.get<ReportDataResponse>(
      `/api/agent/sessions/${sessionId}/reports/${reportId}/data`,
      {
        params: selectedParams
          ? { selected_params: selectedParams }
          : undefined,
      }
    )
  }

  async getFlightOptions(
    sessionId: number,
    ids: number[]
  ): Promise<FlightOptionsResponse> {
    return this.get<FlightOptionsResponse>(
      `/api/agent/sessions/${sessionId}/flight_options`,
      { params: { ids } }
    )
  }

  async getFlights(sessionId: number, ids: number[]): Promise<FlightsResponse> {
    return this.get<FlightsResponse>(
      `/api/agent/sessions/${sessionId}/flights`,
      { params: { ids } }
    )
  }

  async getEvaluations({
    page,
    per_page,
    tags,
    agent_type,
    model_id,
  }: {
    page?: number
    per_page?: number
    tags?: string[]
    agent_type?: string
    model_id?: string
  } = {}): Promise<GetEvaluationsResponse> {
    const searchParams = new URLSearchParams()
    if (page) {
      searchParams.set("page", page.toString())
    }
    if (per_page) {
      searchParams.set("per_page", per_page.toString())
    }
    if (agent_type) {
      searchParams.set("agent_type", agent_type)
    }
    if (model_id) {
      searchParams.set("model_id", model_id)
    }
    // Use array parameter format: ?tags[]=one&tags[]=two
    tags?.forEach((tag) => searchParams.append("tags[]", tag))

    return this.get<GetEvaluationsResponse>(
      `/api/agent/evaluations?${searchParams.toString()}`
    )
  }

  async getEvaluationPickerResults({
    page,
    per_page,
    q,
    tags,
    agent_type,
  }: {
    page?: number
    per_page?: number
    q?: string
    tags?: string[]
    agent_type?: string | null
  } = {}): Promise<GetAgentEvaluationPickerResponse> {
    const searchParams = new URLSearchParams()
    if (page) {
      searchParams.set("page", page.toString())
    }
    if (per_page) {
      searchParams.set("per_page", per_page.toString())
    }
    if (q) {
      searchParams.set("q", q)
    }
    if (agent_type) {
      searchParams.set("agent_type", agent_type)
    }
    tags?.forEach((tag) => searchParams.append("tags[]", tag))

    return this.get<GetAgentEvaluationPickerResponse>(
      `/api/agent/evaluations/picker?${searchParams.toString()}`
    )
  }

  async getEvaluation(id: number): Promise<AgentEvaluationDetail> {
    return this.get<AgentEvaluationDetail>(`/api/agent/evaluations/${id}`)
  }

  async createEvaluation(
    payload: CreateEvaluationPayload
  ): Promise<AgentEvaluationDetail> {
    return this.post<
      AgentEvaluationDetail,
      { evaluation: CreateEvaluationPayload }
    >("/api/agent/evaluations", { evaluation: payload })
  }

  async updateEvaluation(
    id: number,
    payload: UpdateEvaluationPayload
  ): Promise<AgentEvaluationDetail> {
    return this.patch<
      AgentEvaluationDetail,
      { evaluation: UpdateEvaluationPayload }
    >(`/api/agent/evaluations/${id}`, { evaluation: payload })
  }

  async deleteEvaluation(id: number): Promise<void> {
    return this.delete<void>(`/api/agent/evaluations/${id}`)
  }

  async runEvaluation(
    id: number,
    options?: { session_config?: EvaluationSessionConfig }
  ): Promise<RunEvaluationResponse> {
    return this.post<
      RunEvaluationResponse,
      { session_config?: EvaluationSessionConfig } | undefined
    >(
      `/api/agent/evaluations/${id}/run`,
      options?.session_config
        ? { session_config: options.session_config }
        : undefined
    )
  }

  async runEvaluationsBatch(
    selection: RunEvaluationsBatchSelection,
    options?: {
      session_config?: EvaluationSessionConfig
      global_session_config?: EvaluationSessionConfig
      session_config_overrides_by_agent_type?: Record<
        string,
        EvaluationSessionConfig
      >
    }
  ): Promise<{
    runs: Array<{ evaluation_id: number; run_id: number }>
  }> {
    return this.post<{
      runs: Array<{ evaluation_id: number; run_id: number }>
    }>("/api/agent/evaluations/run_batch", {
      selection,
      session_config: options?.session_config,
      global_session_config: options?.global_session_config,
      session_config_overrides_by_agent_type:
        options?.session_config_overrides_by_agent_type,
    })
  }

  async runEphemeralEvaluation(
    payload: CreateEvaluationPayload
  ): Promise<RunEvaluationResponse> {
    return this.post<RunEvaluationResponse, CreateEvaluationPayload>(
      "/api/agent/evaluations/run_ephemeral",
      payload
    )
  }

  async getEvaluationRuns(
    evaluationId: number,
    params?: {
      page?: number
      per_page?: number
      model_id?: string
    }
  ): Promise<GetEvaluationRunsResponse> {
    return this.get<GetEvaluationRunsResponse>(
      `/api/agent/evaluations/${evaluationId}/runs`,
      { params }
    )
  }

  async getEvaluationRun(runId: number): Promise<AgentEvaluationRunDetail> {
    return this.get<AgentEvaluationRunDetail>(
      `/api/agent/evaluation_runs/${runId}`
    )
  }

  async getChatTranscript(chatId: number): Promise<ChatTranscriptResponse> {
    return this.get<ChatTranscriptResponse>(
      `/api/agent/chats/${chatId}/transcript`
    )
  }

  async getEvaluationReport(id: number): Promise<AgentEvaluationReportDetail> {
    return this.get<AgentEvaluationReportDetail>(
      `/api/agent/evaluation_reports/${id}`
    )
  }

  async getEvaluationReportForPrint(
    id: number,
    printToken: string
  ): Promise<AgentEvaluationReportDetail> {
    const searchParams = new URLSearchParams()
    searchParams.set("print_token", printToken)
    const queryString = searchParams.toString()

    return this.get<AgentEvaluationReportDetail>(
      `/api/agent/evaluation_reports/${id}/print?${queryString}`
    )
  }

  async getLatestEvaluationReport(
    reportType: AgentEvaluationReportType = "daily_core"
  ): Promise<AgentEvaluationReportDetail> {
    const searchParams = new URLSearchParams()
    searchParams.set("report_type", reportType)

    return this.get<AgentEvaluationReportDetail>(
      `/api/agent/evaluation_reports/latest?${searchParams.toString()}`
    )
  }

  async listEvaluationReports(
    reportType?: AgentEvaluationReportType
  ): Promise<AgentEvaluationReportsListResponse> {
    const searchParams = new URLSearchParams()
    if (reportType) {
      searchParams.set("report_type", reportType)
    }
    const queryString = searchParams.toString()
    const path = queryString
      ? `/api/agent/evaluation_reports?${queryString}`
      : "/api/agent/evaluation_reports"

    return this.get<AgentEvaluationReportsListResponse>(path)
  }

  async rerunEvaluationReportRun(
    reportId: number,
    agentEvaluationId: number
  ): Promise<AgentEvaluationReportRerunResponse> {
    return this.post<AgentEvaluationReportRerunResponse, undefined>(
      `/api/agent/evaluation_reports/${reportId}/evaluations/${agentEvaluationId}/rerun`,
      undefined
    )
  }

  async batchRead(
    payload: AgentBatchReadPayload
  ): Promise<AgentBatchReadResponse> {
    return this.post<AgentBatchReadResponse, AgentBatchReadPayload>(
      "/api/agent/batch_read",
      payload
    )
  }

  async getAllEvaluationRuns(params?: {
    run_ids?: number[]
    evaluation_id?: number
    status?: EvaluationRunStatus
    from_date?: string
    to_date?: string
    model_id?: string
    page?: number
    per_page?: number
  }): Promise<GetEvaluationRunsResponse> {
    const searchParams = new URLSearchParams()

    // Add array params with [] suffix for Rails
    if (params?.run_ids?.length) {
      params.run_ids.forEach((id) =>
        searchParams.append("run_ids[]", String(id))
      )
    }
    if (params?.evaluation_id) {
      searchParams.append("evaluation_id", String(params.evaluation_id))
    }
    if (params?.status) {
      searchParams.append("status", params.status)
    }
    if (params?.from_date) {
      searchParams.append("from_date", params.from_date)
    }
    if (params?.to_date) {
      searchParams.append("to_date", params.to_date)
    }
    if (params?.model_id) {
      searchParams.append("model_id", params.model_id)
    }
    if (params?.page) {
      searchParams.append("page", String(params.page))
    }
    if (params?.per_page) {
      searchParams.append("per_page", String(params.per_page))
    }

    return this.get<GetEvaluationRunsResponse>(
      `/api/agent/evaluation_runs?${searchParams.toString()}`
    )
  }

  async getEvaluationTags(): Promise<{ tags: string[] }> {
    return this.get<{ tags: string[] }>("/api/agent/evaluations/tags")
  }

  async getEvaluationOrganizations(): Promise<EvaluationOrganizationOption[]> {
    return this.get<EvaluationOrganizationOption[]>(
      "/api/agent/evaluations/organizations"
    )
  }

  async lookupEvaluationUser(query: string): Promise<EvaluationUserLookup> {
    return this.get<EvaluationUserLookup>(
      `/api/agent/evaluations/lookup_user`,
      { params: { q: query } }
    )
  }

  async generateEvalFromSession(payload: {
    session_id: number
    agent_type?: string
    initial_message: string
    is_result_good: boolean
    feedback?: string
    additional_notes?: string
    auto_generate: {
      assertions: boolean
      driver_objective: boolean
      preprocess_instruction: boolean
      tags: boolean
      follow_up_instruction: boolean
    }
  }): Promise<GenerateEvalFromSessionResponse> {
    return this.post<GenerateEvalFromSessionResponse, typeof payload>(
      "/api/agent/evaluations/generate_from_session",
      payload
    )
  }

  async buildEvalFromDescription(payload: {
    description: string
    mode?: "create" | "refine"
    base_eval?: {
      name?: string
      description?: string
      agent_type?: string
      initial_prompt?: string
      assertions?: {
        items: Array<{ assertion: string; severity: string }>
      }
      driver_config?: {
        driver_objective?: string
        key_facts?: string[]
        memories?: Array<{ path: string; key: string; value: string }>
      }
      preprocess_instruction?: string
      follow_up_instruction?: string
      tags?: string[]
    }
    agent_type?: string
    auto_generate?: {
      assertions: boolean
      driver_objective: boolean
      preprocess_instruction: boolean
      tags: boolean
      follow_up_instruction: boolean
    }
  }): Promise<BuildEvalFromDescriptionResponse> {
    return this.post<BuildEvalFromDescriptionResponse, typeof payload>(
      "/api/agent/evaluations/build_from_description",
      payload
    )
  }
}

export interface SandboxHotelAvailability {
  entity_id: string
  room_entity_id: string | null
  room_ref: string | null
  room_name: string | null
  checkin_date: string
  checkout_date: string
  lowest_price_per_night: number | null
  price_per_night_with_taxes_and_fees: number | null
  currency: string
  is_available: boolean
  rooms_remaining: number | null
  price_type: string
  provider: string | null
  supplier: string | null
  match_category: string | null
  is_perfect_match: boolean
}

export interface SandboxHotelRoom {
  room_entity_id: string
  room_ref: string | null
  entity_id: string
  room_name: string
  description: string | null
  room_type: string | null
  num_bedrooms: number | null
  num_bathrooms: number | null
  bed_configuration: unknown
  estimated_occupancy: number | null
  room_size_sqft: number | null
  positive_features: string | null
  negative_features: string | null
  unique_features: string | null
  photo_url: string | null
}

export interface SandboxHotelDataResponse {
  availability: SandboxHotelAvailability[]
  rooms: SandboxHotelRoom[]
  search_group: string
}

export interface MemoryReportColumn {
  name: string
  type: string
  is_entity_id: boolean
}

export interface MemoryReportResponse {
  data: Array<Record<string, unknown>>
  query: string | null
  description: string | null
  columns: MemoryReportColumn[]
  has_config: boolean
}

export interface ReportDataResponse {
  report_name: string
  data: Array<Record<string, unknown>>
  columns: MemoryReportColumn[]
  query: string
  params: Record<string, string[]>
  selected_params: Record<string, string>
}

export interface FlightOptionWithMeta extends FlightOption {
  id: number
  notes: string | null | undefined
  tags: string[] | undefined
  all_slices: FlightSliceApi[]
}

export interface FlightOptionsResponse {
  flight_options: FlightOptionWithMeta[]
}

export interface FlightsResponse {
  flights: FlightLegApi[]
}
